import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Plus, Printer, Send } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Row, RowList, Td, Th, Thead, Tr } from "@/components/steel/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field, Kpi, KpiStrip, PageHeader, StatusBadge } from "@/components/steel"
import { Input } from "@/components/ui/input"
import { formatCurrency } from "@/lib/utils"

type LineItem = {
  amount: number
  description: string
  id: number
  kind: string
  quantity: number
  unitAmount: number
}

type Payment = {
  amount: number
  externalId?: string | null
  id: number
  method?: string | null
  receivedOn?: string | null
}

type Invoice = {
  amountPaid: number
  balance: number
  client: string
  dueDate?: string | null
  id: number
  issueDate?: string | null
  lineItems: LineItem[]
  notes?: string | null
  number: string
  payments: Payment[]
  project: { id: string; name: string }
  status: string
  subtotal: number
  tax: number
  total: number
}

function formatDate(value?: string | null) {
  if (!value) return null
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function ageInDays(value?: string | null): number | null {
  if (!value) return null
  return Math.floor((Date.now() - new Date(`${value}T00:00:00`).getTime()) / 86_400_000)
}

function RecordPaymentDialog({ invoice }: { invoice: Invoice }) {
  const [amount, setAmount] = useState(String(invoice.balance))
  const [externalId, setExternalId] = useState("")
  const [method, setMethod] = useState("")
  const [open, setOpen] = useState(false)
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10))

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Plus />
        Record payment
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
            <DialogDescription>A payment received against {invoice.number}.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Amount">
                <Input
                  className="num text-right"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Received on">
                <Input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
              </Field>
            </div>
            <Field label="Method">
              <Input placeholder="Check, ACH, wire" value={method} onChange={(e) => setMethod(e.target.value)} />
            </Field>
            <Field label="Reference">
              <Input
                placeholder="Check no., confirmation"
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!(parseFloat(amount) > 0) || !receivedOn}
              onClick={() =>
                router.post(
                  `/invoices/${invoice.id}/record_payment`,
                  {
                    amount: parseFloat(amount) || 0,
                    externalId: externalId.trim() || null,
                    method: method.trim() || null,
                    receivedOn,
                  },
                  { preserveScroll: true, onSuccess: () => setOpen(false) },
                )
              }
            >
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default function InvoiceShow({ invoice }: { invoice: Invoice }) {
  const age = ageInDays(invoice.issueDate)

  return (
    <>
      <Head title={invoice.number} />

      <PageHeader
        leading={
          <Button variant="ghost" size="icon-sm" asChild>
            <Link href={`/projects/${invoice.project.id}`}>
              <ArrowLeft />
            </Link>
          </Button>
        }
        title={<span className="num">{invoice.number}</span>}
        meta={
          <span className="flex items-center gap-2.5">
            <StatusBadge status={invoice.status} />
            {invoice.client}
            {formatDate(invoice.dueDate) ? ` · due ${formatDate(invoice.dueDate)}` : ""}
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer />
              Print
            </Button>
            {invoice.status === "draft" ? (
              <Button size="sm" onClick={() => router.post(`/invoices/${invoice.id}/send_invoice`)}>
                <Send />
                Send
              </Button>
            ) : null}
            {invoice.status !== "draft" && invoice.status !== "void" && invoice.balance > 0 ? (
              <RecordPaymentDialog invoice={invoice} />
            ) : null}
          </>
        }
      />

      <KpiStrip>
        <Kpi compact accent label="Total" value={formatCurrency(invoice.total)} />
        <Kpi compact label="Paid" value={formatCurrency(invoice.amountPaid)} />
        <Kpi compact label="Balance" value={formatCurrency(invoice.balance)} />
        <Kpi compact label="Age" value={age != null ? `${age} days` : "—"} />
      </KpiStrip>

      <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="border-border px-6 py-4 pb-7 lg:border-r">
          <h2 className="mb-3 text-title font-semibold">Line items</h2>
          <DataTable minWidth={520}>
            <Thead>
              <Th>Description</Th>
              <Th align="right">Qty</Th>
              <Th align="right">Unit</Th>
              <Th align="right">Amount</Th>
            </Thead>
            <tbody>
              {invoice.lineItems.map((line) => (
                <Tr key={line.id} className="hover:bg-transparent">
                  <Td>
                    <span className="block text-body font-medium">{line.description}</span>
                    <span className="block text-meta capitalize text-muted-foreground">{line.kind}</span>
                  </Td>
                  <Td align="right" num muted>
                    {line.quantity}
                  </Td>
                  <Td align="right" num muted>
                    {formatCurrency(line.unitAmount)}
                  </Td>
                  <Td align="right" num strong>
                    {formatCurrency(line.amount)}
                  </Td>
                </Tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="pt-2.5 text-right text-body font-semibold">
                  Total
                </td>
                <td className="num pt-2.5 text-right text-[14px] font-semibold text-whisper">
                  {formatCurrency(invoice.total)}
                </td>
              </tr>
            </tfoot>
          </DataTable>

          {invoice.notes ? (
            <p className="mt-4 border-t border-border pt-3 text-label text-muted-foreground">{invoice.notes}</p>
          ) : null}
        </div>

        <div className="px-6 py-4 pb-7">
          <h2 className="mb-3 text-title font-semibold">Payments</h2>
          {invoice.payments.length === 0 ? (
            <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
              No payments recorded.
            </p>
          ) : (
            <RowList>
              {invoice.payments.map((payment) => (
                <Row key={payment.id}>
                  <span className="num w-[62px] shrink-0 text-label text-muted-foreground">
                    {formatDate(payment.receivedOn) ?? "—"}
                  </span>
                  <span className="flex-1">
                    <span className="block text-body font-medium">
                      {[payment.method, payment.externalId].filter(Boolean).join(" ") || "Payment"}
                    </span>
                  </span>
                  <span className="num text-body font-medium">{formatCurrency(payment.amount)}</span>
                </Row>
              ))}
            </RowList>
          )}

          <div className="flex items-center justify-between border-t border-border py-2.5">
            <span className="text-body text-muted-foreground">Outstanding</span>
            <span className="num text-body font-medium">{formatCurrency(invoice.balance)}</span>
          </div>
        </div>
      </div>
    </>
  )
}
